import { StyleSheet } from "react-native-unistyles";
import { Banner } from "@visioni/a3s-ui/components";
import { Text, View } from "@src/components";
import { INCIDENT_DEFAULT_DAYS, parseIncidentFilters, useTenantIncidents } from "@visioni/a3s-monitoring/app";
import { patriotToolsHref, TENANT_INCIDENTS_SUBPATH } from "../routes";
import { useTenantListFilters } from "../hooks/useTenantListFilters";
import { ActivationFilterBar } from "../components/ActivationFilterBar";
import { IncidentsTable } from "../components/IncidentsTable";
import { QueryNotice } from "../components/QueryNotice";

/**
 * One organisation's genuine incidents, narrowed by site and camera like the
 * activation list (`?site=&camera=&tz=&from=&to=&order=`). Their totals are a
 * page of their own.
 *
 * Times default to the viewer's own zone even with a site selected, as on the
 * totals, so the two pages read the same days.
 */
export function TenantIncidentsScreen() {
  const { tenantId, values, scope, timeZone, fallback, onChange } = useTenantListFilters({ viewerTimeZone: true });
  const parsed = timeZone === undefined ? undefined : parseIncidentFilters({ ...values, tz: timeZone }, new Date());
  const ok = parsed?.ok === true ? parsed : undefined;

  const list = useTenantIncidents({ filters: ok?.filters ?? { since: "" }, enabled: ok !== undefined });
  return (
    <View style={styles.page}>
      <View style={styles.filterRow}>
        {timeZone !== undefined && (
          <ActivationFilterBar
            values={{ ...values, tz: timeZone }}
            onChange={onChange}
            sites={scope.sites}
            cameras={scope.cameras}
            isLoading={scope.isLoading}
            timeZone={timeZone}
            fallback={fallback}
            offsetsAt={ok !== undefined ? new Date(ok.filters.since) : new Date()}
            fromPlaceholder={`Last ${String(INCIDENT_DEFAULT_DAYS)} days`}
            siteTimeZoneDefault={false}
          />
        )}
      </View>
      {parsed === undefined || timeZone === undefined ?
        <View style={styles.notice}>
          <QueryNotice isLoading error={null} onRetry={list.refetch} />
        </View>
      : !parsed.ok ?
        <View style={styles.notice}>
          <Banner variant="warning" title="Filters not applied" message={parsed.problem} />
        </View>
      : list.items === undefined || list.error !== null ?
        <View style={styles.notice}>
          <QueryNotice isLoading={list.isLoading} error={list.error} onRetry={list.refetch} />
        </View>
      : list.items.length === 0 && !list.pager.canGoBack ?
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted">
            No genuine incidents found
          </Text>
        </View>
      : <IncidentsTable
          items={list.items}
          isStale={list.isPlaceholderData}
          isFetching={list.isFetching}
          hrefFor={(incident) =>
            patriotToolsHref(tenantId, `${TENANT_INCIDENTS_SUBPATH}/${encodeURIComponent(incident.id)}`)
          }
          siteLabel={scope.siteLabel}
          cameraLabel={scope.cameraLabel}
          timeZone={timeZone}
          pager={list.pager}
          nextCursor={list.nextCursor}
        />
      }
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  page: { flex: 1 },
  filterRow: {
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    // Above the list, so the filter dropdowns open over the rows.
    zIndex: 1,
  },
  notice: { paddingHorizontal: theme.spacing.xl },
  centered: { flex: 1, alignItems: "center", justifyContent: "center", padding: theme.spacing["4xl"] },
}));
